import {
  Smartphone, Shirt, Home as HomeIcon, Sparkles, Baby, Dumbbell,
  Laptop, Watch, PenTool, Music, Paintbrush,
  Gamepad2, Gift, UtensilsCrossed, HeartPulse,
  Volume2, Mountain,
} from "lucide-react";

export interface SubCategory {
  name: string;
  emoji: string;
  image: string;
  bannerImage: string;
  description: string;
}

export interface CategoryGroup {
  name: string;
  slug: string;
  icon: typeof Smartphone;
  color: string;
  image: string;
  circleImage: string;
  description: string;
  subcategories: SubCategory[];
}

function slugify(name: string): string {
  return name.replace(/[\s/]+/g, '-').replace(/[^\u0590-\u05FFa-zA-Z0-9-]/g, '').replace(/-+/g, '-');
}

const sub = (name: string, emoji: string, description: string): SubCategory => {
  const slug = slugify(name);
  return {
    name,
    emoji,
    image: `/categories/sub/${slug}.jpg`,
    bannerImage: `/categories/sub/${slug}.jpg`,
    description,
  };
};

export const categoryGroups: CategoryGroup[] = [
  // ─── 1. אלקטרוניקה ───
  {
    name: "אלקטרוניקה",
    slug: "electronics",
    icon: Smartphone,
    color: "from-blue-500 to-cyan-400",
    image: "/categories/electronics.jpg",
    circleImage: "/categories/electronics-circle.webp",
    description: "הגאדג'טים והמכשירים החכמים שהופכים את החיים לקלים, מהירים ומחוברים יותר",
    subcategories: [
      sub("סמארטפונים", "📱", "הטלפונים החכמים החדשים ביותר עם ביצועים מרשימים"),
      sub("טאבלטים", "📲", "טאבלטים לעבודה, לימודים ובידור בכל מקום"),
      sub("אוזניות", "🎧", "חוויית שמע מושלמת עם אוזניות איכותיות"),
      sub("מטענים", "🔌", "מטענים מהירים ואמינים לכל מכשיר"),
      sub("רמקולים", "🔊", "רמקולים ניידים וביתיים לסאונד עוצמתי"),
      sub("מצלמות", "📷", "תפסו כל רגע עם מצלמות מקצועיות וקומפקטיות"),
      sub("מסכים", "📺", "מסכים באיכות גבוהה לעבודה ולבידור"),
      sub("בידוריות", "🎤", "מערכות בידור ביתיות לחוויה קולנועית"),
      sub("אביזרי סלולר", "📱", "כיסויים, מגנים ואביזרים לטלפון שלכם"),
      sub("אביזרי חשמל", "⚡", "שקעים, מפצלים ופתרונות חשמל חכמים"),
      sub("שלטים רחוקים", "📡", "שליטה מרחוק נוחה על כל המכשירים"),
      sub("מקרנים", "🎥", "מקרנים חדים להקרנה ביתית מרהיבה"),
      sub("מצלמות אבטחה", "📹", "שמרו על הבית והעסק עם מצלמות חכמות"),
      sub("סוללות", "🔋", "סוללות עמידות וחזקות לכל שימוש"),
      sub("כבלים ומתאמים", "🔗", "חיבורים ומתאמים לכל סוגי המכשירים"),
      sub("רחפנים", "🚁", "רחפנים עם מצלמה לצילום אווירי מדהים"),
    ],
  },

  // ─── 2. מחשבים ───
  {
    name: "מחשבים",
    slug: "computers",
    icon: Laptop,
    color: "from-indigo-500 to-blue-400",
    image: "/categories/computers.jpg",
    circleImage: "/categories/computers-circle.webp",
    description: "מחשבים, ציוד היקפי ואביזרים לעבודה, גיימינג ויצירתיות",
    subcategories: [
      sub("מחשבים ניידים", "💻", "מחשבים ניידים קלים וחזקים לכל משימה"),
      sub("מסכים", "🖥️", "מסכי מחשב חדים וצבעוניים לעבודה מדויקת"),
      sub("עכברים", "🖱️", "עכברים ארגונומיים ומדויקים לשימוש יומיומי"),
      sub("מקלדות", "⌨️", "מקלדות מכניות ואלחוטיות בתחושה מושלמת"),
      sub("גיימינג", "🎮", "ציוד גיימינג מקצועי לחוויית משחק עוצמתית"),
      sub("אחסון", "💾", "פתרונות אחסון מהירים ואמינים לכל הנתונים"),
      sub("מדפסות", "🖨️", "מדפסות איכותיות לבית ולמשרד"),
      sub("רשתות", "📡", "ראוטרים ומגדילי טווח לאינטרנט מהיר"),
      sub("אביזרי מחשב", "🔧", "אביזרים שמשדרגים את תחנת העבודה"),
      sub("מצלמות רשת", "📷", "מצלמות רשת באיכות HD לשיחות וידאו"),
    ],
  },

  // ─── 3. סאונד ───
  {
    name: "סאונד",
    slug: "sound",
    icon: Volume2,
    color: "from-gray-600 to-gray-400",
    image: "/categories/sound.jpg",
    circleImage: "/categories/sound-circle.webp",
    description: "ציוד הגברה, רמקולים ומערכות סאונד מקצועיות לכל אירוע",
    subcategories: [
      sub("הגברה", "🔊", "מערכות הגברה עוצמתיות לאירועים ומופעים"),
      sub("רמקולים מוגברים", "📢", "רמקולים מוגברים באיכות סטודיו"),
      sub("במות ותאורה", "🎭", "ציוד במה ותאורה ליצירת אווירה מושלמת"),
      sub("מעבדי סאונד", "🎛️", "מעבדי אפקטים ועיבוד שמע מתקדמים"),
      sub("כבלים ומחברים", "🔗", "כבלים מקצועיים לחיבור אמין ואיכותי"),
      sub("מגברים", "🔉", "מגברי כוח לסאונד צלול ועוצמתי"),
      sub("מיקסרים", "🎚️", "מיקסרים אנלוגיים ודיגיטליים לשליטה מלאה"),
      sub("אפקטי תאורה", "💡", "אפקטי תאורה מרהיבים לכל אירוע"),
      sub("מכונות עשן", "💨", "מכונות עשן ואפקטים מיוחדים לאווירה"),
      sub("קייסים ומארזים", "🧳", "קייסים עמידים להגנה על הציוד"),
      sub("עמודי רמקול", "📏", "עמודי רמקול יציבים ומתכווננים"),
    ],
  },

  // ─── 4. מוזיקה ───
  {
    name: "מוזיקה",
    slug: "music",
    icon: Music,
    color: "from-fuchsia-500 to-pink-400",
    image: "/categories/music.jpg",
    circleImage: "/categories/music-circle.webp",
    description: "כלי נגינה, ציוד הקלטה ואביזרי מוזיקה למוזיקאים בכל הרמות",
    subcategories: [
      sub("גיטרות", "🎸", "גיטרות אקוסטיות וחשמליות בסאונד מושלם"),
      sub("קלידים ופסנתרים", "🎹", "פסנתרים דיגיטליים וקלידים למתחילים ומקצוענים"),
      sub("תופים", "🥁", "ערכות תופים אקוסטיות ואלקטרוניות"),
      sub("מיקרופונים", "🎤", "מיקרופונים מקצועיים להקלטה ולהופעות"),
      sub("אביזרי מוזיקה", "🎵", "אביזרים חיוניים לכל מוזיקאי"),
      sub("ציוד הקלטה", "🎙️", "ציוד הקלטה מקצועי לסטודיו ביתי"),
      sub("פדלים ואפקטים", "🎚️", "פדלי אפקט ומעבדים ליצירת צליל ייחודי"),
      sub("מגברי גיטרה", "🔊", "מגברי גיטרה לסאונד עשיר ומלא"),
      sub("עמדות וסטנדים", "🗜️", "עמדות ותושבות יציבות לכלי הנגינה"),
      sub("מיתרים", "🧵", "מיתרים איכותיים לגיטרות וכלי קשת"),
      sub("נרתיקים וקייסים", "🧳", "נרתיקים מרופדים להגנה מלאה על הכלי"),
    ],
  },

  // ─── 5. בית וגן ───
  {
    name: "בית וגן",
    slug: "home-garden",
    icon: HomeIcon,
    color: "from-green-500 to-emerald-400",
    image: "/categories/home-garden.jpg",
    circleImage: "/categories/home-garden-circle.webp",
    description: "כל מה שצריך כדי להפוך את הבית והגינה למקום חלומי ומזמין",
    subcategories: [
      sub("ריהוט", "🪑", "ריהוט מעוצב ונוח לכל חדר בבית"),
      sub("תאורה", "💡", "גופי תאורה מעוצבים ליצירת אווירה חמימה"),
      sub("דקורציה", "🖼️", "פריטי דקורציה שמוסיפים אופי לכל חלל"),
      sub("מצעים", "🛏️", "מצעים רכים ואיכותיים לשינה מפנקת"),
      sub("כלי מטבח", "🍳", "כלי מטבח מקצועיים לבישול ביתי מושלם"),
      sub("מוצרי ניקיון", "🧹", "פתרונות ניקיון חכמים לבית נקי ומבריק"),
      sub("גינון", "🌱", "כלים וציוד לגינה פורחת ומטופחת"),
      sub("הבית", "🏠", "מוצרים חכמים שמשדרגים את חיי הבית"),
      sub("פסלים ועיטורים", "🗿", "פסלים ועיטורים אמנותיים לעיצוב הבית"),
      sub("פסלים", "🗽", "פסלי נוי ואמנות לעיצוב הבית, הגינה והמשרד"),
      sub("מנורות", "🪔", "מנורות דקורטיביות בסגנונות מגוונים"),
      sub("שעוני קיר", "🕐", "שעוני קיר מעוצבים שמשלימים את העיצוב"),
      sub("מתלים ואחסון", "🪝", "פתרונות אחסון יצירתיים לסדר מושלם"),
      sub("כלי גינה", "🌻", "כלי גינה איכותיים לעבודה קלה ויעילה"),
    ],
  },

  // ─── 6. מטבח ───
  {
    name: "מטבח",
    slug: "kitchen",
    icon: UtensilsCrossed,
    color: "from-orange-500 to-red-400",
    image: "/categories/kitchen.jpg",
    circleImage: "/categories/kitchen-circle.webp",
    description: "כלים, מכשירים ואביזרי מטבח שהופכים כל ארוחה לחוויה קולינרית",
    subcategories: [
      sub("מוצרי מטבח", "🍳", "מוצרי מטבח שימושיים לבישול יומיומי"),
      sub("מוצרי חשמל למטבח", "⚡", "מכשירי חשמל חדשניים שחוסכים זמן"),
      sub("בקבוקים וכוסות", "🥤", "בקבוקים וכוסות מעוצבים לכל משקה"),
      sub("סכו״ם וכלי אוכל", "🍽️", "סטים מעוצבים של סכו״ם וכלי הגשה"),
      sub("קומקומים", "☕", "קומקומים חשמליים לכוס תה או קפה מושלמת"),
      sub("בלנדרים ומיקסרים", "🫗", "בלנדרים חזקים למשקאות ומתכונים"),
      sub("סירים ומחבתות", "🫕", "סירים ומחבתות איכותיים לבישול מקצועי"),
      sub("כלי אפייה", "🧁", "תבניות וכלי אפייה ליצירות מתוקות"),
      sub("קופסאות אחסון", "📦", "קופסאות אחסון אטומות לשמירה על טריות"),
      sub("פותחנים ואביזרים", "🍷", "פותחנים ואביזרי מטבח שימושיים"),
      sub("תרמוסים", "🫙", "תרמוסים איכותיים ששומרים על הטמפרטורה"),
      sub("מגשים", "🍱", "מגשי הגשה מעוצבים לאירוח מרשים"),
    ],
  },

  // ─── 7. אופנה ───
  {
    name: "אופנה",
    slug: "fashion",
    icon: Shirt,
    color: "from-pink-500 to-rose-400",
    image: "/categories/fashion.jpg",
    circleImage: "/categories/fashion-circle.webp",
    description: "ביגוד, תיקים ואקססוריז שמשלימים כל לוק ומביאים סטייל לכל יום",
    subcategories: [
      sub("תיקים", "👜", "תיקים אופנתיים ומעשיים לכל אירוע"),
      sub("אקססוריז", "💍", "אקססוריז מעוצבים שמשלימים כל הופעה"),
      sub("כפפות", "🧤", "כפפות חמות ואיכותיות לימים הקרים"),
      sub("צעיפים", "🧣", "צעיפים מעוצבים שמוסיפים צבע ללוק"),
      sub("כובעים", "🧢", "כובעים בסגנונות מגוונים לכל עונה"),
      sub("משקפי שמש", "🕶️", "משקפי שמש אופנתיים עם הגנת UV מלאה"),
      sub("מוצרי חוף", "🏖️", "כל מה שצריך ליום מושלם על החוף"),
      sub("ארנקים", "👛", "ארנקים אלגנטיים ומעשיים לכל יום"),
      sub("תכשיטים", "💎", "תכשיטים מרהיבים שמוסיפים ברק מיוחד"),
      sub("חולצות", "👕", "חולצות בסגנונות מגוונים לכל אירוע"),
      sub("מוצרי חורף", "🧣", "ביגוד ואביזרי חורף לחום ונוחות מקסימלית"),
    ],
  },

  // ─── 8. שעונים ───
  {
    name: "שעונים",
    slug: "watches",
    icon: Watch,
    color: "from-slate-600 to-slate-400",
    image: "/categories/watches.jpg",
    circleImage: "/categories/watches-circle.webp",
    description: "שעונים אלגנטיים, ספורטיביים וחכמים שמשלבים סטייל ופונקציונליות",
    subcategories: [
      sub("שעוני גברים", "⌚", "שעוני גברים אלגנטיים לכל סגנון"),
      sub("שעוני נשים", "⏱️", "שעוני נשים עדינים ומעוצבים"),
      sub("שעונים חכמים", "📟", "שעונים חכמים עם טכנולוגיה מתקדמת"),
      sub("רצועות", "🔗", "רצועות החלפה במגוון חומרים וצבעים"),
      sub("שעוני ספורט", "🏅", "שעוני ספורט עמידים עם מדידת ביצועים"),
      sub("שעוני קיר", "🕰️", "שעוני קיר מעוצבים לכל חלל"),
      sub("שעוני שולחן", "⏰", "שעוני שולחן קלאסיים ומודרניים"),
      sub("קופסאות לשעונים", "🗃️", "קופסאות אחסון מהודרות לאוסף השעונים"),
    ],
  },

  // ─── 9. ספורט ───
  {
    name: "ספורט",
    slug: "sports",
    icon: Dumbbell,
    color: "from-red-500 to-orange-400",
    image: "/categories/sports.jpg",
    circleImage: "/categories/sports-circle.webp",
    description: "ציוד ואביזרי ספורט לאימון, כושר ופעילות גופנית בכל רמה",
    subcategories: [
      sub("כושר", "💪", "ציוד כושר מקצועי לאימון בבית ובחדר כושר"),
      sub("אופניים", "🚴", "אופניים ואביזרים לרכיבה מהנה ובטוחה"),
      sub("קורקינטים", "🛴", "קורקינטים חשמליים ורגילים לנסיעה נוחה"),
      sub("אביזרי ספורט", "🏋️", "אביזרים משלימים לכל סוגי הספורט"),
      sub("עיסוי ושיקום", "💆", "מכשירי עיסוי ושיקום להתאוששות מהירה"),
    ],
  },

  // ─── 10. משחקים ───
  {
    name: "משחקים",
    slug: "games",
    icon: Gamepad2,
    color: "from-violet-500 to-purple-400",
    image: "/categories/games.jpg",
    circleImage: "/categories/games-circle.webp",
    description: "משחקים, צעצועים וערכות יצירה לשעות של כיף והנאה לכל הגילאים",
    subcategories: [
      sub("משחקי חשיבה", "🧩", "משחקי חשיבה מאתגרים שמחדדים את המוח"),
      sub("קליעה למטרה", "🎯", "משחקי קליעה מהנים לכל המשפחה"),
      sub("לוח שש בש ודמקה", "♟️", "משחקי לוח קלאסיים לערבים משפחתיים"),
      sub("עבודות יד", "✂️", "ערכות עבודות יד ליצירתיות אינסופית"),
      sub("יצירה לילדים", "🎨", "ערכות יצירה צבעוניות שמפתחות דמיון"),
      sub("ציור", "🖌️", "ציוד ציור מקצועי לאמנים מתחילים ומנוסים"),
      sub("צעצועים", "🧸", "צעצועים מקוריים ומהנים לילדים"),
      sub("דמויות ופסלונים", "🦸", "דמויות אספנות ופסלונים מפורטים"),
      sub("מכוניות צעצוע", "🏎️", "מכוניות צעצוע ומסלולי מרוץ מרגשים"),
      sub("מכוניות רטרו", "🚗", "מכוניות רטרו אספנותיות בעיצוב קלאסי ונוסטלגי"),
      sub("משחקי קלפים", "🃏", "משחקי קלפים מגוונים לכל גיל"),
      sub("פאזלים", "🧩", "פאזלים מאתגרים בגדלים ורמות שונות"),
      sub("רובוטריקים", "🤖", "רובוטריקים ורובוטים אינטראקטיביים"),
      sub("דגמים ומודלים", "✈️", "ערכות הרכבה ודגמים מפורטים לאספנים"),
      sub("משחקי חוץ", "🪁", "משחקי חוץ מהנים לפעילות באוויר הפתוח"),
      sub("ערכות יצירה", "🎭", "ערכות יצירה מלאות לפרויקטים מיוחדים"),
    ],
  },

  // ─── 11. תינוקות ───
  {
    name: "תינוקות",
    slug: "babies",
    icon: Baby,
    color: "from-yellow-500 to-amber-400",
    image: "/categories/babies.jpg",
    circleImage: "/categories/babies-circle.webp",
    description: "מוצרים איכותיים ובטוחים לתינוקות ולפעוטות, כי הם ראויים לטוב ביותר",
    subcategories: [
      sub("ביגוד תינוקות", "👶", "ביגוד רך ונוח לתינוקות בכל עונה"),
      sub("האכלה", "🥣", "מוצרי האכלה בטוחים ונוחים לשימוש"),
      sub("צעצועי תינוקות", "🧸", "צעצועים מפתחים ובטוחים לתינוקות"),
      sub("מנשאים", "🤱", "מנשאים ארגונומיים לנשיאה נוחה ובטוחה"),
    ],
  },

  // ─── 12. כלי כתיבה ומשרד ───
  {
    name: "כלי כתיבה ומשרד",
    slug: "stationery",
    icon: PenTool,
    color: "from-amber-500 to-orange-400",
    image: "/categories/stationery.jpg",
    circleImage: "/categories/stationery-circle.webp",
    description: "כלי כתיבה, ציוד משרדי ואביזרי ארגון לעבודה יעילה ומסודרת",
    subcategories: [
      sub("עטים", "🖊️", "עטים איכותיים בכתיבה חלקה ונעימה"),
      sub("מחברות ויומנים", "📓", "מחברות ויומנים מעוצבים לכל צורך"),
      sub("ארגוניות", "🗂️", "ארגוניות ומערכות סידור לשולחן מסודר"),
      sub("נייר ומעטפות", "📄", "נייר ומעטפות באיכות מעולה"),
      sub("אביזרי משרד", "📎", "אביזרים קטנים שעושים הבדל גדול"),
      sub("טושים וצבעים", "🖍️", "טושים וצבעים בגוונים עשירים ומגוונים"),
      sub("מגרסות נייר", "📃", "מגרסות נייר להשמדה בטוחה של מסמכים"),
      sub("מחשבונים", "🔢", "מחשבונים מדויקים לעבודה ולימודים"),
      sub("מדבקות וסרטים", "🏷️", "מדבקות וסרטי דבק לכל שימוש"),
    ],
  },

  // ─── 13. מתנות ───
  {
    name: "מתנות",
    slug: "gifts",
    icon: Gift,
    color: "from-rose-500 to-pink-400",
    image: "/categories/gifts.jpg",
    circleImage: "/categories/gifts-circle.webp",
    description: "מתנות מיוחדות ומקוריות לכל אירוע, אדם ותקציב",
    subcategories: [
      sub("מתנות גאדג'טים ומשחקים", "🎁", "גאדג'טים ומשחקים מגניבים למתנה מפתיעה"),
      sub("מוצרי פרסום נאוי", "🎀", "מוצרי פרסום ממותגים באיכות גבוהה"),
      sub("סטים ומארזים", "📦", "מארזי מתנה מפנקים ומרשימים"),
      sub("מזכרות", "🏆", "מזכרות ופריטי אספנות מיוחדים"),
      sub("מתנות לגבר", "🎩", "מתנות מושלמות שכל גבר ישמח לקבל"),
      sub("מתנות לאישה", "💐", "מתנות מפנקות ומרגשות לאישה המיוחדת"),
    ],
  },

  // ─── 14. יופי ובריאות ───
  {
    name: "יופי ובריאות",
    slug: "beauty-health",
    icon: HeartPulse,
    color: "from-pink-400 to-rose-300",
    image: "/categories/health-beauty.jpg",
    circleImage: "/categories/health-beauty-circle.webp",
    description: "מוצרי יופי, טיפוח ובריאות שגורמים לכם להרגיש ולהיראות במיטבכם",
    subcategories: [
      sub("טיפוח עור", "🧴", "מוצרי טיפוח עור לעור בריא וזוהר"),
      sub("כלי יופי", "✨", "כלי יופי מקצועיים לתוצאות מושלמות"),
      sub("מוצרי בריאות", "💊", "מוצרי בריאות ואיכות חיים"),
      sub("איפור", "💄", "מוצרי איפור איכותיים ללוק מושלם"),
      sub("בשמים", "🌸", "בשמים מרהיבים שמשאירים חותם"),
      sub("מוצרי טיפוח", "🧖", "מוצרי טיפוח מפנקים לגוף ולנפש"),
      sub("שיער", "💇", "מוצרי שיער לעיצוב, טיפוח והגנה"),
      sub("ציפורניים", "💅", "לקים וכלי מניקור לציפורניים מושלמות"),
      sub("מכשירי עיסוי", "💆", "מכשירי עיסוי לרגיעה והפגת מתח"),
      sub("מוצרי גילוח", "🪒", "מוצרי גילוח מתקדמים לעור חלק"),
      sub("ציוד רפואי", "🩺", "ציוד רפואי ביתי לניטור ובדיקה"),
    ],
  },

  // ─── 15. קמפינג וטיולים ───
  {
    name: "קמפינג וטיולים",
    slug: "camping-hiking",
    icon: Mountain,
    color: "from-green-600 to-lime-400",
    image: "/categories/camping-hiking.jpg",
    circleImage: "/categories/camping-hiking-circle.webp",
    description: "ציוד קמפינג, טיולים והרפתקאות לחוויות בלתי נשכחות בטבע",
    subcategories: [
      sub("ציוד קמפינג", "⛺", "ציוד קמפינג מקצועי ללילות בטבע"),
      sub("קמפינג", "🏕️", "ציוד ואביזרי קמפינג לחוויות לינה מושלמות בטבע"),
      sub("פנסים", "🔦", "פנסים חזקים לתאורה בכל תנאי"),
      sub("תרמילים", "🎒", "תרמילי גב נוחים ומרווחים לטיולים"),
      sub("כלי בישול שטח", "🍳", "כלי בישול קומפקטיים לארוחות בשטח"),
      sub("סכינים וכלים", "🔪", "סכיני שטח וכלים רב-שימושיים"),
      sub("בקבוקי שתייה", "🫗", "בקבוקי שתייה עמידים לכל מסלול"),
      sub("ביגוד שטח", "🥾", "ביגוד שטח נוח ועמיד לכל מזג אוויר"),
    ],
  },

  // ─── 16. בהתאמה אישית ───
  {
    name: "בהתאמה אישית",
    slug: "custom",
    icon: Paintbrush,
    color: "from-orange-500 to-amber-400",
    image: "/categories/custom.jpg",
    circleImage: "/categories/custom-circle.webp",
    description: "מוצרים בהתאמה אישית עם הדפסה, חריטה ועיצוב ייחודי בדיוק בשבילכם",
    subcategories: [
      sub("חולצות מותאמות", "👕", "חולצות עם הדפסה אישית לכל אירוע"),
      sub("ספלים מותאמים", "☕", "ספלים מעוצבים עם תמונה או טקסט אישי"),
      sub("כריות מותאמות", "🛋️", "כריות נוי מעוצבות בהתאמה אישית"),
      sub("כיסויי טלפון", "📱", "כיסויי טלפון עם עיצוב אישי ומגן"),
      sub("תכשיטים חרוטים", "💍", "תכשיטים עם חריטה אישית ומשמעותית"),
      sub("אלבומי תמונות", "📸", "אלבומי תמונות מעוצבים לשמירת זכרונות"),
      sub("שלטים מותאמים", "🪧", "שלטים מעוצבים עם שם או ברכה אישית"),
      sub("מפתחות חרוטים", "🔑", "מחזיקי מפתחות חרוטים במתנה אישית"),
    ],
  },
];

const HEBREW_CATEGORY_LABEL_REGEX = /^[\u0590-\u05FF׳״'"\-\s]+$/;

export function isValidHebrewCategoryLabel(value?: string | null): boolean {
  if (!value) return false;

  const trimmed = value.trim();
  if (!trimmed) return false;

  const words = trimmed.split(/\s+/).filter(Boolean);
  return words.length > 0 && words.length <= 3 && HEBREW_CATEGORY_LABEL_REGEX.test(trimmed);
}

export const validCategoryGroups: CategoryGroup[] = categoryGroups
  .filter((group) => isValidHebrewCategoryLabel(group.name))
  .map((group) => ({
    ...group,
    subcategories: group.subcategories.filter((sub) => isValidHebrewCategoryLabel(sub.name)),
  }));
